/*
 * Copyright © 2025 AionTrack. All rights reserved.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { Project, CreateProjectInput, UpdateProjectInput } from '@/types/database'
import { useAnalytics } from './useAnalytics'

// Fetch projects for current user
// RLS policies ensure users only see projects they own (owner_user_id = auth.uid())
// Admin users do NOT see all projects here - use useAdminProjects() in /admin instead
export function useProjects() {
  return useQuery({
    queryKey: ['projects', 'v2'], // v2 = cache buster after RLS policy fix
    queryFn: async () => {
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) throw error
      return data as Project[]
    },
    // Refetch when window regains focus to ensure fresh data
    refetchOnWindowFocus: true,
    // Consider data stale after 30 seconds
    staleTime: 30000,
  })
}

// Fetch single project
export function useProject(projectId: string | undefined) {
  return useQuery({
    queryKey: ['projects', projectId],
    queryFn: async () => {
      if (!projectId) throw new Error('Project ID is required')

      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .eq('id', projectId)
        .maybeSingle()

      if (error) {
        throw error
      }
      if (!data) {
        throw new Error('Project not found')
      }
      return data as Project
    },
    enabled: !!projectId,
  })
}

// Create project mutation
export function useCreateProject() {
  const queryClient = useQueryClient()
  const { trackProjectCreated } = useAnalytics()
  const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || 'http://localhost:8000'

  return useMutation({
    mutationFn: async (project: CreateProjectInput) => {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) throw new Error('Not authenticated')

      const response = await fetch(`${BACKEND_URL}/api/projects`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(project),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to create project' }))
        throw new Error(errorData.detail || 'Failed to create project')
      }

      const data = await response.json()
      return data as Project
    },
    onSuccess: async (data) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })

      // Track project creation in PostHog
      trackProjectCreated({
        project_id: data.id,
        nle_type: data.nle_type,
      })

      // Activity logging is now handled by backend
      // Invalidate activity logs to show the new entry
      queryClient.invalidateQueries({ queryKey: ['activityLogs', data.id] })
    },
  })
}

// Update project mutation
export function useUpdateProject() {
  const queryClient = useQueryClient()
  const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || 'http://localhost:8000'

  return useMutation({
    mutationFn: async ({ id, ...updates }: UpdateProjectInput & { id: string }) => {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) throw new Error('Not authenticated')

      const response = await fetch(`${BACKEND_URL}/api/projects/${id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to update project' }))
        throw new Error(errorData.detail || 'Failed to update project')
      }

      const data = await response.json()
      return data as Project
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      queryClient.invalidateQueries({ queryKey: ['projects', data.id] })
      // Activity logging is now handled by backend
      queryClient.invalidateQueries({ queryKey: ['activityLogs', data.id] })
    },
  })
}

// Delete project mutation
export function useDeleteProject() {
  const queryClient = useQueryClient()
  const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || 'http://localhost:8000'

  return useMutation({
    mutationFn: async (projectId: string) => {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) throw new Error('Not authenticated')

      const response = await fetch(`${BACKEND_URL}/api/projects/${projectId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to delete project' }))
        throw new Error(errorData.detail || 'Failed to delete project')
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
    },
  })
}
